import { useState, type FC } from "react";
import type {
  Parameter,
  ParameterExclusionOption,
} from "../../../models/AmlParameter";
import { useCreateAmlRuleReferenceExclusion } from "../../../graphql/hooks/aml/useCreateAmlRuleReferenceExclusion";
import dayjs from "dayjs";
import { Row } from "react-grid-system";
import BodySmall from "../../atoms/typography/BodySmall";
import Small from "../../atoms/typography/Small";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import DatePicker from "../filters/DatePicker";
import Checkbox from "../../atoms/inputs/Checkbox";
import TextField from "../../atoms/inputs/TextField";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";

interface Props {
  parameter: Parameter;
  riskScoreId: string;
  ruleType: string;
  clientIdentifier: string;
  parameterExclusionOptions?: ParameterExclusionOption;
  hasActiveExclusion?: boolean;
  refetchRiskScore?: () => void;
}

const RiskScoreParameter: FC<Props> = ({
  parameter,
  riskScoreId,
  ruleType,
  clientIdentifier,
  parameterExclusionOptions,
  hasActiveExclusion,
  refetchRiskScore,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [tbcChecked, setTbcChecked] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | string>("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onSuccessCreateExclusion = (result?: any) => {
    if (result?.aml?.createAmlRuleReferenceExclusion?.success) {
      setComment("");
      setErrorMessage("");
      setSuccessMessage(t("aml.exclusionCreatedSuccessfully"));
      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    } else {
      setErrorMessage(t("aml.theExclusionCouldNotBeCreated"));
    }
  };
  const onErrorCreateExclusion = (e?: any) => {
    setErrorMessage(e?.toString());
  };
  const { createRiskScoreExclusion, error, loading } =
    useCreateAmlRuleReferenceExclusion({
      onCompleted: onSuccessCreateExclusion,
      onError: onErrorCreateExclusion,
    });

  const onClickSaveException = async () => {
    try {
      await createRiskScoreExclusion({
        variables: {
          input: {
            referenceIdentifier: parameter.id,
            comment: comment,
            expiresAt: tbcChecked
              ? null
              : dayjs(selectedDate).format("YYYY-MM-DD"),
            riskScoreId: riskScoreId,
            rule: ruleType,
            clientIdentifier: clientIdentifier,
          },
        },
      });
    } catch (error) {
      setErrorMessage((error as string).toString());
    }
  };

  const onSelectDate = (date: any) => {
    setTbcChecked(false);
    const newDate = new Date(date);
    setSelectedDate(newDate);
  };

  const onCheckTbc = () => {
    setTbcChecked(!tbcChecked);
    setSelectedDate("");
  };

  return (
    <div>
      <div>
        <BodySmall>{parameter.title}</BodySmall>
        {hasActiveExclusion && (
          <Small color={theme.colors.info}>
            {t("aml.currentExceptionExists")}
          </Small>
        )}
      </div>

      <div>
        <HorizontalSpacer spacing={0.5} />
        {parameter.information?.map((info, index) => (
          <div key={index}>
            <BodySmall>{info.description}</BodySmall>
            <HorizontalSpacer spacing={0.5} />
          </div>
        ))}

        {parameterExclusionOptions && !hasActiveExclusion && (
          <div>
            {
              <div>
                <Small color={theme.colors.textSecondary}>
                  {t("aml.youCanCreateExclusionsOnIrrelevantRules")}
                </Small>
                <HorizontalSpacer spacing={1} />
              </div>
            }
            <Row nogutter justify="between" align="end">
              <DatePicker
                value={selectedDate ? new Date(selectedDate) : undefined}
                onChange={(d) => onSelectDate(d)}
                disableDatesBeforeToday
                maxDate={
                  parameterExclusionOptions.latestAllowedExpirationDate ??
                  undefined
                }
              />

              {!parameterExclusionOptions.latestAllowedExpirationDate && (
                <Checkbox
                  title={t("aml.validUntilFurtherNotice")}
                  checked={tbcChecked}
                  onChange={onCheckTbc}
                />
              )}
            </Row>
            <HorizontalSpacer spacing={1} />
            <TextField
              placeholder={t("common.comment")}
              onChange={(value) => setComment(value)}
              value={comment}
              fullWidth
            />
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="end">
              <OutlinedButton
                title={t("aml.createExclusion")}
                color={theme.colors.primary}
                disabled={
                  comment.length < 3 || (selectedDate === "" && !tbcChecked)
                }
                onClick={onClickSaveException}
                loading={loading}
              />
            </Row>
            <HorizontalSpacer spacing={1} />
          </div>
        )}
      </div>

      {(errorMessage || error) && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={errorMessage || error?.toString()}
        />
      )}
      {successMessage && (
        <Alert
          type="success"
          title={successMessage}
          message={
            <OutlinedButton
              title={t("aml.refresh")}
              onClick={() => refetchRiskScore && refetchRiskScore()}
              color={theme.colors.primary}
            />
          }
        />
      )}
    </div>
  );
};

export default RiskScoreParameter;
